import { useCallback, useEffect, useRef, useState } from "react";
import {
  FILE_LIBRARY_CHAT_POLL_INTERVAL_MS,
  FILE_LIBRARY_DOCUMENT_CHAT_LABEL,
  getFileLibraryConversation,
  getFileLibraryDocumentMessage,
  postFileLibraryDocumentMessage,
  fileLibraryChatPollTimeoutMs,
  fileLibraryDocumentChatEnabled,
} from "@/api/file_library_document_chat";
import type { FileLibraryDocument } from "@/api/file_library";
import { Button } from "@/components/ui/button";
import { OrdinalIcon } from "@/components/OrdinalIcon";
import {
  ChatAssistantBubble,
  ChatComposer,
  ChatInlineAlert,
  ChatLoadingIndicator,
  ChatMessageThread,
  ChatUserBubble,
  ChatWelcomeBubble,
  chatMessageFromFileLibrary,
  isVisibleInFileLibraryThread,
  type ChatDisplayMessage,
} from "@/components/chat/primitives";
import { FileText, X } from "lucide-react";

const FILE_LIBRARY_WELCOME =
  "Ask questions about this PDF. Answers use only this file—not your full document library.";

type FileCenterDocumentChatPaneProps = {
  municipalityId: number;
  document: FileLibraryDocument;
  onClose: () => void;
};

export function FileCenterDocumentChatPane({
  municipalityId,
  document,
  onClose,
}: FileCenterDocumentChatPaneProps) {
  const [messages, setMessages] = useState<ChatDisplayMessage[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeAssistantId, setActiveAssistantId] = useState<number | null>(null);
  const [documentTextReady, setDocumentTextReady] = useState(true);
  const [documentTextFailureReason, setDocumentTextFailureReason] = useState<string | null>(null);
  const [documentTextTruncated, setDocumentTextTruncated] = useState(false);
  const [truncationNoticeDismissed, setTruncationNoticeDismissed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatEnabled = fileLibraryDocumentChatEnabled(document);

  const refreshConversation = useCallback(async () => {
    const payload = await getFileLibraryConversation(municipalityId, document.id);
    setMessages(payload.messages.map(chatMessageFromFileLibrary));
    setDocumentTextReady(payload.document_text_ready);
    setDocumentTextFailureReason(payload.document_text_failure_reason ?? null);
    setDocumentTextTruncated(payload.document_text_truncated === true);
    return payload;
  }, [document.id, municipalityId]);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    refreshConversation()
      .then((payload) => {
        if (cancelled) return;
        // Resume polling a reply that was in flight when the page was refreshed.
        const inFlight = [...payload.messages]
          .reverse()
          .find((message) => message.role === "assistant" && (message.status === "pending" || message.status === "processing"));
        if (inFlight) {
          setActiveAssistantId(inFlight.id);
          setLoading(true);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : "Could not load chat.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [refreshConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (activeAssistantId == null || !loading) return;

    const assistantId = activeAssistantId;
    const started = Date.now();
    const pollTimeoutMs = fileLibraryChatPollTimeoutMs(document.page_count);

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;

      if (Date.now() - started > pollTimeoutMs) {
        setLoading(false);
        setActiveAssistantId(null);
        setLoadError("This is taking longer than expected. Try again.");
        return;
      }

      try {
        if (!documentTextReady) {
          const conversation = await getFileLibraryConversation(municipalityId, document.id);
          setDocumentTextReady(conversation.document_text_ready);
          setDocumentTextFailureReason(conversation.document_text_failure_reason ?? null);
          setDocumentTextTruncated(conversation.document_text_truncated === true);
        }

        const result = await getFileLibraryDocumentMessage(municipalityId, document.id, assistantId);
        if (result.status === "completed" || result.status === "failed") {
          await refreshConversation();
          setLoading(false);
          setActiveAssistantId(null);
          return;
        }
      } catch (err) {
        setLoading(false);
        setActiveAssistantId(null);
        setLoadError(err instanceof Error ? err.message : "Could not retrieve the answer.");
        return;
      }

      if (!cancelled) {
        window.setTimeout(poll, FILE_LIBRARY_CHAT_POLL_INTERVAL_MS);
      }
    };

    const timeoutId = window.setTimeout(poll, FILE_LIBRARY_CHAT_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [activeAssistantId, loading, municipalityId, document.id, document.page_count, documentTextReady, refreshConversation]);

  const handleSubmit = async () => {
    const trimmed = query.trim();
    if (!trimmed || !chatEnabled || loading) return;

    setLoading(true);
    setLoadError(null);

    try {
      const result = await postFileLibraryDocumentMessage(municipalityId, document.id, trimmed);
      await refreshConversation();
      setQuery("");

      if (result.status === "pending") {
        setActiveAssistantId(result.assistant_message_id);
        return;
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
      setLoadError(err instanceof Error ? err.message : "Could not send your message.");
    }
  };

  const loadingMessage = documentTextReady ? "Working on your question…" : "Preparing this file for chat…";

  const composerAlerts = (
    <>
      {loadError ? (
        <ChatInlineAlert variant="destructive">{loadError}</ChatInlineAlert>
      ) : null}
      {documentTextFailureReason ? (
        <ChatInlineAlert variant="destructive">{documentTextFailureReason}</ChatInlineAlert>
      ) : null}
      {documentTextTruncated && !truncationNoticeDismissed ? (
        <ChatInlineAlert
          onDismiss={() => setTruncationNoticeDismissed(true)}
          dismissLabel="Dismiss"
        >
          Only the first portion of this file is used for answers; very long documents may miss content toward the
          end.
        </ChatInlineAlert>
      ) : null}
    </>
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-background" data-testid="file-center-document-chat">
      <div className="flex h-11 shrink-0 items-center gap-2 border-b px-3">
        <OrdinalIcon className="h-6 w-6 shrink-0" fill="hsl(var(--primary) / 0.08)" />
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold">{FILE_LIBRARY_DOCUMENT_CHAT_LABEL}</h2>
          <p className="truncate text-xs text-muted-foreground flex items-center gap-1">
            <FileText className="h-3 w-3 shrink-0" aria-hidden />
            {document.name}
          </p>
        </div>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onClose} aria-label="Close chat">
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>

      <ChatMessageThread compact messageCount={messages.length} scrollAnchorRef={messagesEndRef}>
        {messages.length === 0 && chatEnabled ? (
          <ChatWelcomeBubble message={FILE_LIBRARY_WELCOME} compact />
        ) : null}
        {messages.filter(isVisibleInFileLibraryThread).map((message) =>
          message.role === "user" ? (
            <ChatUserBubble
              key={message.id}
              content={message.content}
              createdAt={message.createdAt}
              compact
            />
          ) : (
            <ChatAssistantBubble
              key={message.id}
              content={message.content}
              createdAt={message.createdAt}
              compact
            />
          ),
        )}
        {loading ? <ChatLoadingIndicator message={loadingMessage} compact /> : null}
      </ChatMessageThread>

      <div className="shrink-0 border-t p-4">
        <ChatComposer
          value={query}
          onChange={setQuery}
          onSubmit={handleSubmit}
          placeholder={chatEnabled ? "Ask a question about this PDF…" : "Chat unavailable for this document"}
          disabled={!chatEnabled}
          loading={loading}
          loadingLabel="Asking…"
          ariaLabel="Question about this document"
          compact
          alerts={composerAlerts}
        />
      </div>
    </div>
  );
}
